/**
 * Backend API client for OTP, Nafath, and RER operations
 *
 * Environment detection:
 * - If VITE_API_URL is set → production mode (calls NestJS backend)
 * - Otherwise → development mode (uses hardcoded mocks)
 *
 * Hostname-based fallback:
 * - dallali.com / www.dallali.com → production
 * - dev.dallali.com → dev API
 * - staging.dallali.com → staging API
 * - Otherwise → development (no API)
 */

import { jsonOrEmpty, safeJson } from './safeJson';
import type {
  RerValidateRequest,
  RerVerifyRequest,
  RerSearchRequest,
  RerSaveVerificationRequest,
  RerLinkVerificationRequest,
  RerApiResponse,
  RerVerifyResponse,
  RerSearchResponse,
  RerVerificationRecord,
} from '@/types/rer';
import type { RegaValidateRequest, RegaApiResponse, RegaValidateResponse, RegaComplianceRequest, RegaComplianceResponse } from '@/types/rega';
import type { PropertyMediaEntityType, UploadPropertyImageResponse } from '@dallali/shared';
import { consentStatusSchema, type ConsentStatusDto } from '@dallali/shared';
import type { User, UserIdentity } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { normalizePhoneForDB, toGoTruePhone } from '@/lib/phone';
import type {
  NotificationDispatchRequest,
  NotificationDispatchResponse,
} from '@dallali/notifications';
import type {
  HatifAssignConversationRequest,
  ChannelItem,
  Contact,
  HatifConversation,
  HatifConversationTimelineEvent,
  HatifCreateConversationRequest,
  HatifCreateConversationResult,
  HatifCreateTagRequest,
  HatifListConversationsQuery,
  HatifSearchContactsRequest,
  HatifSendWhatsAppTemplateRequest,
  HatifSendWhatsAppTemplateResult,
  HatifSendWhatsAppTextRequest,
  HatifSendWhatsAppTextResult,
  HatifSetContactPropertyValueRequest,
  HatifTag,
  HatifUpdateTagRequest,
  PagedQuery,
  PagedResult,
  SortedPagedQuery,
  VoxaTemplate,
} from '@dallali/notifications';

function getApiUrl(): string | null {
  // Explicit env var takes priority (used in both dev and production)
  const envUrl = import.meta.env.VITE_API_URL;
  // Trim whitespace/newlines — CI/CD secrets can include trailing newlines that
  // produce an invalid URL string and cause Safari to throw "The string did not
  // match the expected pattern." instead of a recoverable network error.
  if (envUrl) return envUrl.trim();

  // Fallback to hostname detection. Guard `window` — this runs at module load
  // (`export const API_URL = getApiUrl()`), and node-environment tests
  // (`@vitest-environment node`) that transitively import a flipped repository
  // would otherwise crash with "window is not defined" before any test runs.
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const hostMap: Record<string, string> = {
    'dallali.com': 'https://p1.dallali.com',
    'www.dallali.com': 'https://p1.dallali.com',
    'dev.dallali.com': 'https://dev-api.dallali.com',
    'staging.dallali.com': 'https://staging-api.dallali.com',
  };
  if (hostMap[hostname]) return hostMap[hostname];

  // Default: no API URL (pure frontend mock mode)
  return null;
}

export const API_URL = getApiUrl();

/**
 * isProduction — true when a NestJS backend URL is configured.
 * Falls back to no-op / local mock only when API_URL is null (no backend at all).
 * VITE_DEV_MODE no longer gates real backend calls — the backend owns mock decisions.
 */
export const isProduction = API_URL !== null;

/** True when VITE_DEV_MODE=true — bypasses auth in ProtectedRoute. */
export const IS_DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true';

const API_KEY = import.meta.env.VITE_API_KEY || '';

interface SendOtpResponse {
  success: boolean;
  message: string;
}

interface VerifyOtpResponse {
  success: boolean;
  verified: boolean;
  message?: string;
}

/**
 * Verification-only OTP send for PUBLIC flows — POST /auth/send-otp.
 *
 * Mints NO session: it proves the visitor controls the phone number before a
 * lead / service request / property enquiry is accepted. Used by
 * PhoneStep, OtpStep, DemoLeadCaptureModal, PropertyPublicPage and InterestForm.
 *
 * For LOGIN use `nestRequestOtp` + `nestVerifyOtp`, which mint a session.
 *
 * (This function was annotated `@deprecated — use supabase.auth.signInWithOtp`
 * until 2026-07-26. That was wrong after D4: GoTrue is decommissioned, so
 * following it breaks the flow.)
 */
export async function sendOtp(phone: string): Promise<SendOtpResponse> {
  if (!isProduction) {
    return { success: true, message: 'OTP sent (mock)' };
  }

  try {
    const response = await fetch(`${API_URL}/auth/send-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify({ phone }),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to send OTP');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Verification-only OTP check for PUBLIC flows — POST /auth/verify-otp.
 *
 * The counterpart of `sendOtp`: confirms the code and returns `{ verified }`.
 * Mints NO session. For LOGIN use `nestVerifyOtp`, which returns a token pair.
 *
 * (This function was annotated `@deprecated — use supabase.auth.verifyOtp`
 * until 2026-07-26. That was wrong after D4: GoTrue is decommissioned, so
 * following it breaks the flow.)
 */
export async function verifyOtp(phone: string, code: string): Promise<VerifyOtpResponse> {
  if (!isProduction) {
    // Pure-frontend mock mode ONLY (no VITE_API_URL at all — `pnpm run dev`
    // with no backend). `pnpm dev:local` sets VITE_API_URL, so real flows never
    // reach this. Kept because that mock mode is a supported way to run the
    // public pages without an API.
    return {
      success: true,
      verified: code === '123456',
    };
  }

  try {
    const response = await fetch(`${API_URL}/auth/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify({ phone, code }),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to verify OTP');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

// --- D1 Nest Session Authority — auth client (the only login path) ---

/** Token pair minted by the Nest session authority. Mirrors the API's SessionTokenPair. */
export interface NestSessionTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: 'bearer';
}

export interface NestMeResponse {
  userId: string | null;
  companyId: string | null;
  role: string | null;
  isSuperAdmin: boolean;
}

/** The Nest auth path requires a configured backend URL — fail loudly if missing. */
function requireApiUrl(): string {
  if (!API_URL) {
    throw new Error(
      'VITE_API_URL is not configured — Nest is the auth authority and requires ' +
        'a backend URL. Set VITE_API_URL (and VITE_API_KEY).',
    );
  }
  return API_URL;
}

async function nestErrorMessage(response: Response, fallback: string): Promise<string> {
  const error = (await jsonOrEmpty(response)) as { message?: string };
  return error.message || fallback;
}

/**
 * Error thrown by {@link nestRequestOtp}. Carries the HTTP `status` so callers
 * can reliably detect a rate-limit (429) without string-matching the response
 * body — the NestJS throttler returns the generic English message
 * "ThrottlerException: Too Many Requests", which the old `"429"/"rate"/"seconds"`
 * substring checks never matched.
 */
export class NestApiError extends Error {
  /** HTTP status code from the response (e.g. 429 for a throttler rate-limit). */
  readonly status: number;
  /** Backend error code from the response body, when present. */
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'NestApiError';
    this.status = status;
    this.code = code;
  }
}

/** D1: send an OTP via the Nest session authority (POST /auth/request-otp). */
export async function nestRequestOtp(phone: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${requireApiUrl()}/auth/request-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: JSON.stringify({ phone }),
  });
  if (!response.ok) {
    const body = (await jsonOrEmpty(response)) as {
      message?: string;
      code?: string;
    };
    throw new NestApiError(
      body.message || 'Failed to send verification code',
      response.status,
      body.code,
    );
  }
  return safeJson(response);
}

/**
 * D1: verify an OTP and mint a Supabase-compatible session (POST /auth/verify-otp-d1).
 * NOTE: this is the token-minting endpoint — distinct from the deprecated
 * /auth/verify-otp (Unifonic-only, no JWT mint).
 */
export async function nestVerifyOtp(phone: string, code: string): Promise<NestSessionTokens> {
  const response = await fetch(`${requireApiUrl()}/auth/verify-otp-d1`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: JSON.stringify({ phone, code }),
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Invalid verification code'));
  }
  return safeJson(response);
}

/**
 * D1: owner-signup — verify OTP + atomically create landing-owner if new, then
 * mint session (POST /auth/signup-owner-d1).
 * Unlike nestVerifyOtp, this endpoint CREATES the user + profile + owner role
 * in MAIN company if the phone is not yet registered. Idempotent for existing users.
 */
export async function nestSignupOwner(phone: string, code: string): Promise<NestSessionTokens> {
  const response = await fetch(`${requireApiUrl()}/auth/signup-owner-d1`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: JSON.stringify({ phone, code }),
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Owner signup failed'));
  }
  return safeJson(response);
}

/**
 * D1: validate an email/password credential and mint a Supabase-compatible
 * session (POST /auth/login-password-d1). The email/password counterpart to
 * {@link nestVerifyOtp} — mints a Nest session WITH an app.nest_sessions row so
 * the Nest-mode refresh loop can rotate it (GoTrue's own session could not be
 * Nest-refreshed, which force-logged-out email/password users at first refresh).
 */
export async function nestLoginPassword(email: string, password: string): Promise<NestSessionTokens> {
  const response = await fetch(`${requireApiUrl()}/auth/login-password-d1`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Invalid email or password'));
  }
  return safeJson(response);
}

/** Throw a {@link NestApiError} (status + backend code) for a failed auth call. */
async function throwNestApiError(response: Response, fallback: string): Promise<never> {
  const body = (await jsonOrEmpty(response)) as { message?: string; code?: string };
  throw new NestApiError(body.message || fallback, response.status, body.code);
}

/**
 * Email a password-reset link (POST /auth/password-reset/request). The only way
 * to set or change an email password — used by the email login page's "Forgot
 * password?" and by the profile. The API answers `{ success: true }` whether or
 * not the email has an account, so callers must word their confirmation as
 * "if an account exists…" unless the email is the signed-in user's own.
 * Throws {@link NestApiError} (429 when rate-limited).
 */
export async function nestRequestPasswordReset(email: string): Promise<{ success: boolean }> {
  const response = await fetch(`${requireApiUrl()}/auth/password-reset/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) await throwNestApiError(response, 'Failed to send reset link');
  return safeJson(response);
}

/**
 * Set a new password from an emailed reset link
 * (POST /auth/password-reset/confirm). The link is single-use; the API revokes
 * every existing session of the user, so the caller should send them to log in.
 * Throws {@link NestApiError} — code `auth-028` when the link is invalid/used/expired.
 */
export async function nestConfirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<{ success: boolean }> {
  const response = await fetch(`${requireApiUrl()}/auth/password-reset/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: JSON.stringify({ token, newPassword }),
  });
  if (!response.ok) await throwNestApiError(response, 'Failed to reset password');
  return safeJson(response);
}

export type ConsentPurpose =
  | 'signup'
  | 'login_reconfirm'
  | 'owner_signup'
  | 'invite_signup'
  | 'marketing_preference'
  | 'withdrawal';

export type ConsentChannel = 'otp' | 'email_password' | 'owner_signup' | 'invite' | 'reconfirm' | 'profile';

/**
 * Record an explicit PDPL consent event for the authenticated user
 * (POST /auth/consent). The server writes one row each for Terms and Privacy
 * at their current versions, plus the marketing box when `marketing` is given
 * (accepted or declined). Versions are server-side only.
 *
 * Fire-and-forget — never throws; a backend failure returns
 * { success: false } without blocking the caller's flow.
 */
export async function recordConsent(params: {
  purpose: ConsentPurpose;
  channel: ConsentChannel;
  /** The optional marketing box; omit when it was not shown. */
  marketing?: boolean;
  accessToken: string;
}): Promise<{ success: boolean }> {
  if (!isProduction) {
    console.info('[consent] mock recorded', {
      purpose: params.purpose,
      channel: params.channel,
      marketing: params.marketing,
    });
    return { success: true };
  }
  try {
    const response = await fetch(`${requireApiUrl()}/auth/consent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${params.accessToken}`,
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify({
        purpose: params.purpose,
        channel: params.channel,
        marketing: params.marketing,
      }),
    });
    if (!response.ok) return { success: false };
    return (await safeJson(response)) as { success: boolean };
  } catch {
    // Non-blocking — consent failure must never block login.
    return { success: false };
  }
}

export type ConsentStatus = ConsentStatusDto;

/**
 * Fail-open default: a status outage must never lock users behind a dialog.
 * Versions are unknown here (empty) — only the server knows the current ones.
 */
const CONSENT_STATUS_UNKNOWN: ConsentStatus = {
  hasConsent: false,
  needsReconfirm: false,
  marketingOptIn: false,
  marketingAnswered: false,
  versions: { terms: '', privacy: '' },
};

/**
 * The signed-in user's consent position (GET /auth/consent/status). Drives
 * the accept-the-updated-terms dialog and the profile marketing toggle.
 *
 * Fails open: any failure (local dev without an API, or a response that
 * does not match the shared `consentStatusSchema`) returns
 * `needsReconfirm: false`.
 */
export async function getConsentStatus(accessToken: string): Promise<ConsentStatus> {
  if (!isProduction) return CONSENT_STATUS_UNKNOWN;
  try {
    const response = await fetch(`${requireApiUrl()}/auth/consent/status`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-API-Key': API_KEY,
      },
    });
    if (!response.ok) return CONSENT_STATUS_UNKNOWN;
    const parsed = consentStatusSchema.safeParse(await safeJson(response));
    return parsed.success ? parsed.data : CONSENT_STATUS_UNKNOWN;
  } catch {
    return CONSENT_STATUS_UNKNOWN;
  }
}

/**
 * Error thrown by {@link nestRefreshSession}. Carries `status` so callers can
 * distinguish a genuine auth failure (401/403 → re-login, no retry) from a
 * transient transport failure (status 0 = network/API-unreachable) or a 5xx
 * (API restarting mid-deploy) that is worth retrying before signing out.
 */
export class NestRefreshError extends Error {
  /** HTTP status code, or 0 for a transport/network failure (no response). */
  readonly status: number;
  /** Backend auth error code from the response body (e.g. 'auth-006' invalid/
   *  expired, 'auth-009' reuse-detected). Undefined for transport failures. */
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'NestRefreshError';
    this.status = status;
    this.code = code;
  }
}

/** D1: rotate a Nest-issued refresh token, returning a fresh session pair (POST /auth/refresh). */
export async function nestRefreshSession(
  refreshToken: string,
  accessToken?: string | null,
): Promise<NestSessionTokens> {
  let response: Response;
  try {
    response = await fetch(`${requireApiUrl()}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
      // Only include access_token when present — server treats it as optional and
      // behaves exactly as before when it is absent.
      body: JSON.stringify(
        accessToken
          ? { refresh_token: refreshToken, access_token: accessToken }
          : { refresh_token: refreshToken },
      ),
    });
  } catch (e) {
    // Transport failure (DNS, connection refused, API restarting mid-deploy) —
    // there is no HTTP status. Surface status 0 so callers can retry instead of
    // logging the user out on a blip.
    throw new NestRefreshError(e instanceof Error ? e.message : 'Network error', 0);
  }
  if (!response.ok) {
    // Parse the body once to surface BOTH the message and the backend auth code
    // (auth-006 invalid/expired vs auth-009 reuse-detected) for the lost-session tag.
    const body = (await jsonOrEmpty(response)) as { message?: string; code?: string };
    throw new NestRefreshError(
      body.message || 'Failed to refresh session',
      response.status,
      body.code,
    );
  }
  return safeJson(response);
}

/**
 * Delay (ms) before the single retry in {@link refreshWithRetry}.
 *
 * 1.5s is a deliberate balance, not arbitrary: long enough to clear the brief
 * connection-refused gap at the moment pm2 swaps the process (the socket is down
 * for ~1s during `startOrRestart`), short enough that the user barely notices.
 * It is NOT meant to outlast a full multi-second restart — the SERVER-side 60s
 * rotation grace window is what covers the long tail; this retry only needs to
 * survive the instant the socket is unavailable. A single retry (not a backoff
 * loop) keeps a genuinely-down API from hanging the UI on the auth path.
 */
export const REFRESH_RETRY_DELAY_MS = 1500;

/**
 * Refresh the session, retrying ONCE on a transient failure before giving up.
 *
 * A deploy restarts the Nest API; a refresh that hits that ~10–60s window fails
 * with a network error (status 0) or a 5xx. Thanks to the server-side rotation
 * grace window, the same refresh token is still valid for a few seconds, so a
 * single retry after a short delay recovers the session instead of forcing a
 * logout. A real 401/403 (token genuinely invalid) is NOT retried — re-login is
 * the only recovery, so we fail fast.
 */
export async function refreshWithRetry(
  refreshToken: string,
  prevToken?: string | null,
  accessToken?: string | null,
): Promise<NestSessionTokens> {
  try {
    return await nestRefreshSession(refreshToken, accessToken);
  } catch (e) {
    const status = e instanceof NestRefreshError ? e.status : 0;

    // Transient transport / API-restarting failure → retry once with the SAME
    // token after a short delay; the server-side reuse-overlap covers the rest.
    if (status === 0 || status >= 500) {
      await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
      return await nestRefreshSession(refreshToken, accessToken);
    }

    // Genuine 401 on the CURRENT token. The server-side reissue-on-reuse
    // (nest-session.service.ts refreshSession reuse_detected branch) now
    // recovers from a superseded token when a valid access token is presented,
    // so a client-side prev-token retry is no longer needed. Removing it also
    // fixes telemetry contamination (the prev retry's error was replacing the
    // original, relabeling a benign auth-006 as reuse_detected). Fail fast on
    // any 401 — let the caller's signOut path handle it.

    throw e;
  }
}

/**
 * Fetch the full GoTrue-compatible User from Nest GET /auth/me.
 * Replaces supabase.auth.getUser() which round-trips to GoTrue GET /auth/v1/user.
 * Returns null on 401 or network error — caller treats as unauthenticated.
 *
 * When no Nest API URL is configured (GoTrue mode), falls back to the original
 * supabase.auth.getUser() call so the helper is safe in all environments.
 *
 * The Nest endpoint returns the full GoTrue-compatible User shape (merged #1968)
 * plus legacy NestMeResponse fields. Only the User-typed subset is returned.
 */
export async function nestGetUser(): Promise<User | null> {
  // Fall back to GoTrue getUser() when no Nest API is configured
  if (!API_URL) {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  }

  const {
    data: { session },
  } = await supabase.auth.getSession(); // local token retrieval, no GoTrue call
  if (!session?.access_token) return null;

  try {
    const response = await fetch(`${requireApiUrl()}/auth/me`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
    });
    if (response.status === 401) return null;
    if (!response.ok) {
      throw new Error(await nestErrorMessage(response, 'Failed to fetch user'));
    }
    const body = (await safeJson(response)) as Record<string, unknown>;
    // Map the full Nest response to the GoTrue User interface.
    // Required fields are populated from the Nest shape; optional fields not
    // returned (new_email, new_phone, etc.) are undefined — callers guard
    // these with truthiness checks already.
    return {
      id: body.id as string,
      aud: (body.aud as string) ?? 'authenticated',
      role: (body.role as string) ?? 'authenticated',
      email: (body.email as string) ?? null,
      phone: (body.phone as string) ?? null,
      app_metadata:
        (body.app_metadata as Record<string, unknown>) ?? {},
      user_metadata:
        (body.user_metadata as Record<string, unknown>) ?? {},
      created_at: (body.created_at as string) ?? null,
      updated_at: (body.updated_at as string) ?? null,
      last_sign_in_at: (body.last_sign_in_at as string) ?? null,
      email_confirmed_at: (body.email_confirmed_at as string) ?? null,
      phone_confirmed_at: (body.phone_confirmed_at as string) ?? null,
      confirmed_at: (body.email_confirmed_at as string) ?? null,
      identities: (body.identities as UserIdentity[]) ?? [],
    } as User;
  } catch {
    // Network error / API unreachable — caller handles null
    return null;
  }
}

/**
 * Response shape of `GET /auth/permissions` — locked by PR #1648. The frontend
 * consumes only `permissions`; `role`/`companyId`/`isSuperAdmin` are returned
 * for parity/diagnostics but the FE resolves those independently.
 */
export interface NestPermissionsResponse {
  permissions: string[];
  role: string | null;
  companyId: string | null;
  isSuperAdmin: boolean;
}

/**
 * Fetch the caller's effective permission slugs via Nest `GET /auth/permissions`.
 *
 * Replaces the direct `supabase.rpc('get_user_permissions', …)` call that
 * `usePermissions` used to make: the app-wide permission gate now resolves
 * through Nest — the same `AuthzService` the server-side `PermissionGuard` uses —
 * with company + role derived server-side from the verified session (Great
 * Migration N-6). This removes the FE permission read's PostgREST dependency.
 *
 * Fail-closed contract (identical to the prior RPC path):
 *   - 401 (unauthenticated / mid-signout) → `[]`, so every gate reads false.
 *   - Any other non-ok response → throw. React Query then keeps the last cached
 *     result (or the `[]` default on a cold start) — a transient Nest blip does
 *     not silently widen access, and a cold-start failure fails closed.
 *
 * Env-guard fallback: when NO Nest API URL is configured (`API_URL === null`,
 * i.e. pure local mock mode with no backend) the helper falls back to the
 * original `get_user_permissions` RPC so local dev keeps working. This is an
 * ENVIRONMENT guard (mirrors {@link nestGetUser} / {@link nestSignOut}), NOT a
 * runtime feature flag — in every deployed env (dev/staging/prod, `API_URL`
 * set) the permission read is Nest-only, satisfying the N-6 acceptance
 * criterion "no PostgREST/Realtime dependency in the permission path".
 *
 * @param userId    caller's auth user id (only used by the local-mock fallback)
 * @param companyId active company id     (only used by the local-mock fallback)
 */
export async function getPermissions(
  userId: string,
  companyId: string,
): Promise<string[]> {
  // Env-guard fallback: no Nest backend configured → original Supabase RPC.
  if (!API_URL) {
    const { data, error } = await supabase.rpc('get_user_permissions', {
      user_uuid: userId,
      company_uuid: companyId,
    });
    if (error) throw error;
    return (data ?? []) as string[];
  }

  const {
    data: { session },
  } = await supabase.auth.getSession(); // local token retrieval, no GoTrue call
  if (!session?.access_token) return []; // no session → fail closed

  const response = await fetch(`${requireApiUrl()}/auth/permissions`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    },
  });

  // Unauthenticated → no permissions (fail closed), matching nestGetUser().
  if (response.status === 401) return [];
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Failed to fetch permissions'));
  }

  const body = (await safeJson(response)) as NestPermissionsResponse;
  return body.permissions ?? [];
}

/**
 * Sign out of the Nest session.
 *
 * `scope` (default `'global'`):
 *  - `'global'` — revoke ALL of the user's server-side Nest sessions (every
 *    device/tab) via `POST /auth/logout`, then clear THIS client. Use only for
 *    an explicit, user-initiated "Sign Out".
 *  - `'local'`  — clear THIS client only; do NOT revoke server-side sessions.
 *    Use for INVOLUNTARY sign-outs (a failed token refresh, a roleless- or
 *    deactivated-company guard, a pre-flight session reset). A transient hiccup
 *    on one device must not tear down the user's OTHER devices/tabs — and a
 *    spurious server-side revoke deletes every nest_sessions row, orphaning
 *    other tabs' refresh tokens so they fail their next /auth/refresh (the
 *    auth-006 "nest session lost" cascade).
 */
export async function nestSignOut(opts?: { scope?: 'local' | 'global' }): Promise<void> {
  const scope = opts?.scope ?? 'global';

  if (!API_URL) {
    // No Nest backend (GoTrue mode) — honour the same local/global distinction.
    await supabase.auth.signOut({ scope });
    return;
  }

  // Revoke ALL server-side Nest sessions (best-effort) — explicit sign-out only.
  if (scope === 'global') {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession(); // local token retrieval, no GoTrue call
      if (session?.access_token) {
        await fetch(`${requireApiUrl()}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY,
          },
        });
      }
    } catch {
      // Network error / API unreachable — still clear local state below
    }
  }

  // Clear local state WITHOUT the GoTrue network call
  await supabase.auth.signOut({ scope: 'local' });
}

/** D1: resolve the authenticated identity from a Bearer access token (GET /auth/me). */
export async function nestGetMe(accessToken: string): Promise<NestMeResponse> {
  const response = await fetch(`${requireApiUrl()}/auth/me`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Failed to resolve identity'));
  }
  return safeJson(response);
}

// --- Identity API (B3 — replaces 7 Supabase edge functions) ---

/** Lazily resolve the identity API base URL so module import doesn't throw. */
function identityApiUrl(): string {
  if (!API_URL) throw new Error(
    'VITE_API_URL is not configured — the identity API requires a backend URL.',
  );
  return `${API_URL}/identity`;
}

/** Demo-login: return demo credentials (no auth required). */
export async function identityDemoLogin(): Promise<{ email: string; password: string }> {
  const response = await fetch(`${identityApiUrl()}/demo-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Demo login failed'));
  }
  return safeJson(response);
}

/** Delete a user account (super-admin only). */
export async function identityDeleteUser(userId: string, accessToken?: string): Promise<{ success: boolean }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  } else {
    headers['X-API-Key'] = API_KEY;
  }
  const response = await fetch(`${identityApiUrl()}/delete-user`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ userId }),
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Delete user failed'));
  }
  return safeJson(response);
}

/** Delete an admin account (super-admin only). */
export async function identityDeleteAdmin(targetUserId: string, accessToken?: string): Promise<{ success: boolean }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  } else {
    headers['X-API-Key'] = API_KEY;
  }
  const response = await fetch(`${identityApiUrl()}/delete-admin`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ targetUserId }),
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Delete admin failed'));
  }
  return safeJson(response);
}

/** Update a user's phone number atomically. */
export async function identityUpdateUserPhone(
  user_id: string,
  new_phone: string,
  accessToken?: string,
): Promise<{ success: boolean }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  } else {
    headers['X-API-Key'] = API_KEY;
  }
  const response = await fetch(`${identityApiUrl()}/update-user-phone`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ user_id, new_phone }),
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Update phone failed'));
  }
  return safeJson(response);
}

/** Revoke a pending admin invitation (super-admin only). */
export async function identityRevokeAdminInvitation(
  invitationId: string,
  accessToken?: string,
): Promise<{ success: boolean; deleted_user_id?: string }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  } else {
    headers['X-API-Key'] = API_KEY;
  }
  const response = await fetch(`${identityApiUrl()}/revoke-admin-invitation`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ invitationId }),
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Revoke invitation failed'));
  }
  return safeJson(response);
}

/** Delete a company and all related data (super-admin only). */
export async function identityDeleteCompany(companyId: string, accessToken?: string): Promise<{ success: boolean }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  } else {
    headers['X-API-Key'] = API_KEY;
  }
  const response = await fetch(`${identityApiUrl()}/delete-company`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ companyId }),
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Delete company failed'));
  }
  return safeJson(response);
}

// --- Nafath (نفاذ) identity verification ---

interface NafathRequestResponse {
  success: boolean;
  transId?: string;
  random?: string;
  message?: string;
}

export interface NafathIdentityData {
  firstName?: string;
  secondName?: string;
  thirdName?: string;
  lastName?: string;
  englishFirstName?: string;
  englishSecondName?: string;
  englishThirdName?: string;
  englishLastName?: string;
  gender?: string;
  dateOfBirthG?: string;
  dateOfBirthH?: string;
  nationalityCode?: number;
  nationalityDesc?: string;
}

interface NafathStatusResponse {
  success: boolean;
  status?: string;
  data?: NafathIdentityData;
  message?: string;
}

/**
 * Request Nafath identity verification for a national ID.
 * In production: calls NestJS backend → Nafath MFA API.
 * In development: returns a mock transId and random code.
 */
export async function requestNafathVerification(
  nationalId: string,
  userId?: string,
  companyId?: string,
  traceId?: string,
): Promise<NafathRequestResponse> {
  if (!isProduction) {
    const mockRandom = Math.floor(10 + Math.random() * 90).toString();
    return { success: true, transId: 'mock-trans-id', random: mockRandom };
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    };
    // CompositeAuthGuard prefers Bearer over X-API-Key: it populates req.user?.id
    // from the JWT, which the Nafath controller uses as the row's user_id. Without
    // a session JWT the row is written with user_id = NULL, which blocks the SP
    // invite gate (migration 20260621280000: requires a COMPLETED row matching user_id).
    const { data: { session: nafathSession } } = await supabase.auth.getSession();
    if (nafathSession?.access_token) {
      headers['Authorization'] = `Bearer ${nafathSession.access_token}`;
    }
    if (traceId) headers['X-Signup-Trace'] = traceId;

    const response = await fetch(`${API_URL}/nafath/request`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ nationalId, userId, companyId }),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to request Nafath verification');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Check Nafath verification status by polling.
 * In production: calls NestJS backend → Nafath status API.
 * In development: always returns COMPLETED immediately.
 */
export async function checkNafathStatus(
  nationalId: string,
  transId: string,
  random: string,
  traceId?: string,
): Promise<NafathStatusResponse> {
  if (!isProduction) {
    return {
      success: true,
      status: 'COMPLETED',
      data: {
        firstName: 'محمد',
        secondName: 'عبدالله',
        lastName: 'الدلالي',
        englishFirstName: 'MOHAMMED',
        englishSecondName: 'ABDULLAH',
        englishLastName: 'ALDALLALI',
        gender: 'M',
        dateOfBirthG: '1990-01-15',
        dateOfBirthH: '15-06-1410',
        nationalityCode: 113,
        nationalityDesc: 'سعودي',
      },
    };
  }

  try {
    const statusHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    };
    // Same as requestNafathVerification: Bearer lets CompositeAuthGuard tag
    // the nafath_verifications row with the correct user_id (SP gate requirement).
    const { data: { session: nafathStatusSession } } = await supabase.auth.getSession();
    if (nafathStatusSession?.access_token) {
      statusHeaders['Authorization'] = `Bearer ${nafathStatusSession.access_token}`;
    }
    if (traceId) statusHeaders['X-Signup-Trace'] = traceId;

    const response = await fetch(`${API_URL}/nafath/status`, {
      method: 'POST',
      headers: statusHeaders,
      body: JSON.stringify({ nationalId, transId, random }),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to check Nafath status');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

// --- RER (السجل العقاري) Real Estate Registry ---

/**
 * Quick validation of real estate title ownership.
 */
export async function rerValidateTitle(
  data: RerValidateRequest,
): Promise<RerApiResponse> {
  if (!isProduction) {
    return { success: true, data: { status: "Valid" } };
  }

  try {
    const response = await fetch(`${API_URL}/rer/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to validate title');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Full property verification with detailed info.
 */
export async function rerVerifyTitle(
  data: RerVerifyRequest,
  traceId?: string,
): Promise<RerApiResponse<RerVerifyResponse>> {
  if (!isProduction) {
    return {
      success: true,
      data: {
        realEstateNumber: data.realEstateNumber,
        areaSize: 450,
        propertyUse: 'سكني',
        propertyUseCode: 1,
        propertyTypeAr: 'أرض',
        propertyTypeEn: 'Land',
        propertyStatus: 'مسجل',
        firstRegistrationDateH: '15-06-1440',
        firstRegistrationDateG: '20-02-2019',
        cityAr: 'الرياض',
        cityEn: 'Riyadh',
        districtAr: 'العليا',
        districtEn: 'Al Olaya',
        parcelNumber: '1234',
        blockNumber: '5678',
        planNumber: '9012',
        version: 1,
        ownerInformation: [
          {
            ownerNameAr: 'محمد عبدالله الدلالي',
            ownerNameEn: 'Mohammed Abdullah Aldallali',
            ownerId: data.idNumber,
            ownershipPercentage: 100,
            ownerTypeCode: 1,
          },
        ],
        unitDetails: [
          {
            unitNumber: '101',
            unitTypeAr: 'شقة',
            unitTypeEn: 'Apartment',
            unitArea: 120,
          },
          {
            unitNumber: '102',
            unitTypeAr: 'شقة',
            unitTypeEn: 'Apartment',
            unitArea: 95,
          },
        ],
        borderInfo: [
          { direction: 'شمال', length: '20', borderTypeAr: 'شارع', borderTypeEn: 'Street' },
          { direction: 'جنوب', length: '20', borderTypeAr: 'جار', borderTypeEn: 'Neighbor' },
          { direction: 'شرق', length: '22.5', borderTypeAr: 'شارع', borderTypeEn: 'Street' },
          { direction: 'غرب', length: '22.5', borderTypeAr: 'جار', borderTypeEn: 'Neighbor' },
        ],
      },
    };
  }

  try {
    const verifyHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    };
    if (traceId) verifyHeaders['X-Signup-Trace'] = traceId;

    const response = await fetch(`${API_URL}/rer/verify`, {
      method: 'POST',
      headers: verifyHeaders,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to verify title');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Search properties by owner ID.
 */
export async function rerSearchProperties(
  data: RerSearchRequest,
  traceId?: string,
): Promise<RerApiResponse<RerSearchResponse>> {
  if (!isProduction) {
    return {
      success: true,
      data: {
        propertiesCount: 3,
        page: 1,
        pageSize: 10,
        pageCount: 1,
        properties: [
          {
            realEstateNumber: '1100012345678901',
            cityAr: 'الرياض',
            cityEn: 'Riyadh',
            districtAr: 'العليا',
            districtEn: 'Al Olaya',
            areaSize: 450,
            propertyTypeAr: 'أرض',
            propertyTypeEn: 'Land',
            // Land has no units
          },
          {
            realEstateNumber: '2100098765432101',
            cityAr: 'جدة',
            cityEn: 'Jeddah',
            districtAr: 'الحمراء',
            districtEn: 'Al Hamra',
            areaSize: 1200,
            propertyTypeAr: 'عمارة سكنية',
            propertyTypeEn: 'Residential Building',
            unitDetails: [
              { unitNumber: '101', unitTypeAr: 'شقة', unitTypeEn: 'Apartment', unitArea: 120 },
              { unitNumber: '102', unitTypeAr: 'شقة', unitTypeEn: 'Apartment', unitArea: 95 },
              { unitNumber: '103', unitTypeAr: 'استوديو', unitTypeEn: 'Studio', unitArea: 45 },
              { unitNumber: 'G01', unitTypeAr: 'محل', unitTypeEn: 'Shop', unitArea: 80 },
            ],
          },
          {
            realEstateNumber: '3100011223344551',
            cityAr: 'الدمام',
            cityEn: 'Dammam',
            districtAr: 'الشاطئ',
            districtEn: 'Al Shati',
            areaSize: 600,
            propertyTypeAr: 'فيلا',
            propertyTypeEn: 'Villa',
            unitDetails: [
              { unitNumber: 'V01', unitTypeAr: 'فيلا', unitTypeEn: 'Villa', unitArea: 450 },
              { unitNumber: 'V02', unitTypeAr: 'ملحق سائق', unitTypeEn: 'Driver Quarter', unitArea: 35 },
            ],
          },
        ],
      },
    };
  }

  try {
    const searchHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    };
    if (traceId) searchHeaders['X-Signup-Trace'] = traceId;

    const response = await fetch(`${API_URL}/rer/search`, {
      method: 'POST',
      headers: searchHeaders,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to search properties');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Save a verification result to the database.
 */
export async function rerSaveVerification(
  data: RerSaveVerificationRequest,
  traceId?: string,
): Promise<RerApiResponse> {
  if (!isProduction) {
    return { success: true, id: 'mock-' + crypto.randomUUID() };
  }

  try {
    const saveHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    };
    if (traceId) saveHeaders['X-Signup-Trace'] = traceId;

    const response = await fetch(`${API_URL}/rer/verifications/save`, {
      method: 'POST',
      headers: saveHeaders,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to save verification');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * List saved verifications for a company.
 */
export async function rerListVerifications(
  companyId: string,
  limit = 20,
  offset = 0,
): Promise<RerApiResponse<RerVerificationRecord[]>> {
  if (!isProduction) {
    const mockRecords: RerVerificationRecord[] = Array.from({ length: 5 }, (_, i) => ({
      id: `mock-${i + 1}`,
      real_estate_number: `${1100012345678901 + i}`,
      rer_response_json: {},
      verification_date: new Date(Date.now() - i * 86400000).toISOString(),
      status: 'مسجل',
      property_type_ar: ['أرض', 'شقة', 'فيلا', 'عمارة', 'أرض'][i],
      property_type_en: ['Land', 'Apartment', 'Villa', 'Building', 'Land'][i],
      property_status: 'مسجل',
      city_ar: ['الرياض', 'جدة', 'الدمام', 'مكة', 'المدينة'][i],
      city_en: ['Riyadh', 'Jeddah', 'Dammam', 'Makkah', 'Madinah'][i],
      district_ar: 'العليا',
      district_en: 'Al Olaya',
      area_size: [450, 320, 600, 1200, 500][i],
      primary_owner_name_ar: 'محمد عبدالله',
      primary_owner_name_en: 'Mohammed Abdullah',
      total_owners: 1,
      is_linked_to_property: i < 2,
      dallali_property_id: i < 2 ? `prop-${i}` : undefined,
      created_at: new Date(Date.now() - i * 86400000).toISOString(),
    }));
    return { success: true, data: mockRecords, total: 5 };
  }

  try {
    const params = new URLSearchParams({
      companyId,
      limit: String(limit),
      offset: String(offset),
    });
    const response = await fetch(`${API_URL}/rer/verifications?${params}`, {
      method: 'GET',
      headers: {
        'X-API-Key': API_KEY,
      },
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to list verifications');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Get a single verification record by ID.
 */
export async function rerGetVerification(
  id: string,
): Promise<RerApiResponse<RerVerificationRecord>> {
  if (!isProduction) {
    return {
      success: true,
      data: {
        id,
        real_estate_number: '1100012345678901',
        rer_response_json: {},
        verification_date: new Date().toISOString(),
        status: 'مسجل',
        property_type_ar: 'أرض',
        property_type_en: 'Land',
        city_ar: 'الرياض',
        city_en: 'Riyadh',
        district_ar: 'العليا',
        district_en: 'Al Olaya',
        area_size: 450,
        primary_owner_name_ar: 'محمد عبدالله',
        primary_owner_name_en: 'Mohammed Abdullah',
        total_owners: 1,
        is_linked_to_property: false,
        created_at: new Date().toISOString(),
      },
    };
  }

  try {
    const response = await fetch(`${API_URL}/rer/verifications/${id}`, {
      method: 'GET',
      headers: {
        'X-API-Key': API_KEY,
      },
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to get verification');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Link a saved verification to a Dallali property.
 */
export async function rerLinkVerification(
  id: string,
  data: RerLinkVerificationRequest,
): Promise<RerApiResponse> {
  if (!isProduction) {
    return { success: true };
  }

  try {
    const response = await fetch(`${API_URL}/rer/verifications/${id}/link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'Failed to link verification');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

// --- REGA (هيئة العقار) Ad License Validation ---

/**
 * Validate an ad license number with REGA.
 * In production: calls NestJS backend → REGA API.
 * In development: returns mock response.
 */
export async function regaValidateLicense(
  data: RegaValidateRequest,
): Promise<RegaApiResponse<RegaValidateResponse>> {
  if (!isProduction) {
    // Mock mirrors real REGA nested envelope: { Header, Body: { result: { isValid, advertisement } } }
    return {
      success: true,
      data: {
        Body: {
          result: {
            isValid: true,
            advertisement: {
              adLicenseNumber: data.adLicenseNumber,
              advertiserId: data.advertiserId,
              advertiserName: 'شركة اختبار العقارات',
              advertiserType: 'Company',
              advertisementType: 'Sell',
              deedNumber: '810117041224',
              brokerageContractNumber: 'BC-2025-001234',
              brokerageAndMarketingLicenseNumber: '6200000027',
              propertyType: 'Villa',
              propertyArea: 500,
              propertyPrice: 65000,
              propertyFace: 'شمالي',
              propertyAge: '5',
              propertyUsages: ['سكني'],
              propertyUtilities: ['كهرباء', 'ماء', 'صرف صحي', 'ألياف بصرية'],
              channels: ['إلكتروني'],
              streetWidth: '15',
              planNumber: '2341',
              landNumber: '78',
              isConstrained: false,
              obligationsOnTheProperty: '',
              guaranteesAndTheirDuration: 'ضمان هيكلي 10 سنوات',
              complianceWithTheSaudiBuildingCode: 'متوافق',
              adLicenseUrl: 'https://eservicesredp.rega.gov.sa/public/adlicense/view?id=mock',
              titleDeedTypeName: 'صك إلكتروني',
              phoneNumber: '+966500000000',
              adSource: 'منصة دلالي',
              notes: '',
              locationDescriptionOnMOJDeed: 'قطعة أرض سكنية رقم 78 من مخطط 2341',
              ownershipTransferFeeType: 'على المشتري',
              responsibleEmployeeName: 'أحمد محمد',
              responsibleEmployeePhoneNumber: '+966501234567',
              creationDate: '01/01/2025',
              endDate: '01/01/2026',
              location: {
                region: 'منطقة الرياض',
                city: 'الرياض',
                district: 'العليا',
                postalCode: '12211',
                street: 'شارع الأمير محمد بن عبدالعزيز',
                buildingNumber: '1234',
                additionalNumber: '5678',
                longitude: 46.6753,
                latitude: 24.7136,
              },
              borders: {
                northLimitName: 'شارع',
                northLimitDescription: 'شارع عرض 15م',
                northLimitLengthChar: '25',
                eastLimitName: 'جار',
                eastLimitDescription: 'قطعة رقم 79',
                eastLimitLengthChar: '20',
                westLimitName: 'جار',
                westLimitDescription: 'قطعة رقم 77',
                westLimitLengthChar: '20',
                southLimitName: 'شارع',
                southLimitDescription: 'شارع فرعي عرض 10م',
                southLimitLengthChar: '25',
              },
              rerBorders: [
                { type: 'شارع', length: 25, direction: 'شمال' },
                { type: 'جار', length: 20, direction: 'شرق' },
                { type: 'جار', length: 20, direction: 'غرب' },
                { type: 'شارع', length: 25, direction: 'جنوب' },
              ],
            },
          },
        },
      } as unknown as RegaValidateResponse,
    };
  }

  try {
    const response = await fetch(`${API_URL}/rega/validate-license`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || `REGA API error (HTTP ${response.status})`);
    }

    return await safeJson(response);
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error(String(error) || 'REGA validate-license request failed');
  }
}

/**
 * Send REGA compliance notification (DisplayAd, UpdateAd, CancelAd).
 * Fire-and-forget from the caller's perspective.
 * In development: returns mock success.
 */
export async function regaSendCompliance(
  data: RegaComplianceRequest,
): Promise<RegaApiResponse<RegaComplianceResponse>> {
  if (!isProduction) {
    console.log('[REGA Mock] Compliance:', data.operationType, data.adLicenseNumber);
    return {
      success: true,
      data: { isSuccess: true },
    };
  }

  try {
    const response = await fetch(`${API_URL}/rega/compliance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await jsonOrEmpty(response);
      throw new Error(error.message || 'REGA compliance call failed');
    }

    return await safeJson(response);
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Send invitation notification via SMS (Unifonic) and/or email (SendGrid).
 * Non-blocking: caller should catch and not fail the invite flow.
 * Requires backend endpoint: POST /invite/notify
 */
export async function sendInviteNotification(params: {
  phone?: string;
  email?: string;
  name: string;
  inviteLink: string;
  channel: "sms" | "email" | "both";
  // Profile type the user is invited AS (owner / tenant / employee /
  // service_provider). Forwarded to the API so the invite picks the
  // per-portal-type template; unknown / staff roles fall back to generic copy.
  role?: string;
  subject?: string;
  emailBody?: string;
  smsBody?: string;
}): Promise<{ success: boolean; sent: boolean }> {
  if (!isProduction) {
    return { success: true, sent: false }; // no-op in dev
  }

  const canonical = params.phone ? normalizePhoneForDB(params.phone) : null;
  const phoneE164 = canonical ? toGoTruePhone(canonical) : params.phone;

  const response = await fetch(`${API_URL}/invite/notify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": API_KEY,
    },
    body: JSON.stringify({ ...params, phone: phoneE164 }),
  });

  if (!response.ok) {
    const error = await jsonOrEmpty(response);
    throw new Error((error as { message?: string }).message || "Failed to send invite notification");
  }

  return safeJson(response);
}

// ─── Invoice Notification ─────────────────────────────────────────────────────

/**
 * Send invoice notification via SMS and/or email.
 * Logs delivery to invoice_notifications table for tracking.
 */
export async function sendInvoiceNotification(params: {
  invoiceId: string;
  companyId: string;
  channel: "sms" | "email" | "both";
  phone?: string;
  email?: string;
  recipientName: string;
  recipientType: "tenant" | "owner";
  invoiceNumber?: string;
  description?: string;
  totalAmount?: number;
  dueDate?: string;
  paymentLink: string;
  viewLink: string;
  sentBy?: string;
}): Promise<{ success: boolean; sent: string[]; failed: string[] }> {
  if (!isProduction) {
    return { success: true, sent: [], failed: [] }; // no-op in dev
  }

  // NestJS DTO requires strict E.164 (`+966XXXXXXXXX`). DB phones are
  // canonical (`966...` no `+`), so convert at this boundary.
  const canonical = params.phone ? normalizePhoneForDB(params.phone) : null;
  const phoneE164 = canonical ? toGoTruePhone(canonical) : params.phone;

  const response = await fetch(`${API_URL}/invoice-notification/send`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": API_KEY,
    },
    body: JSON.stringify({ ...params, phone: phoneE164 }),
  });

  if (!response.ok) {
    const error = await jsonOrEmpty(response);
    throw new Error((error as { message?: string }).message || "Failed to send invoice notification");
  }

  return safeJson(response);
}

// ─── EJAR Import ──────────────────────────────────────────────────────────────

export interface EjarContractExtracted {
  ejar_contract_number: string | null;
  contract_type: 'residential' | 'commercial' | null;
  start_date: string | null;
  end_date: string | null;
  annual_rent: number | null;
  total_amount: number | null;
  vat_amount: number | null;
  payment_frequency: 'monthly' | 'quarterly' | 'semi_annual' | 'annual' | null;
  payment_schedule: Array<{
    due_date: string;
    amount: number;
    installment_number: number;
  }>;
  property_address: string | null;
  deed_number: string | null;
  deed_issuer: string | null;
  deed_issue_date: string | null;
  deed_type: string | null;
  national_address: string | null;
  property_type: string | null;
  property_usage: string | null;
  property_units_count: number | null;
  property_floors_count: number | null;
  unit_number: string | null;
  unit_type: string | null;
  floor_number: string | null;
  area_sqm: number | null;
  units: Array<{
    unit_number: string | null;
    unit_type: string | null;
    area_sqm: number | null;
    floor_number: string | null;
  }>;
  tenant_name: string | null;
  tenant_id_number: string | null;
  tenant_phone: string | null;
  tenant_type: 'individual' | 'commercial' | null;
  tenant_commercial_name: string | null;
  owner_name: string | null;
  owner_id_number: string | null;

  // Lessor commercial entity + representative (LLM path; optional). When the
  // lessor is a company, `owner_name` (the person) is null and the company sits
  // in `lessor_commercial_name` — the review card must surface these.
  lessor_type?: 'individual' | 'commercial' | null;
  lessor_commercial_name?: string | null;
  lessor_unified_number?: string | null;
  lessor_cr_no?: string | null;
  lessor_representative_name?: string | null;
  lessor_representative_id_number?: string | null;

  // Sublease (عقد من الباطن) parent/child — optional.
  is_sublease?: boolean | null;
  parent_ejar_contract_number?: string | null;

  // Additional fields
  tenant_nationality: string | null;
  tenant_id_type: string | null;
  tenant_email: string | null;
  organization_type: string | null;
  unified_number: string | null;
  cr_no: string | null;
  cr_date: string | null;
  brokerage_fee: number | null;
  security_deposit: number | null;
  number_of_payments: number | null;
  electricity_annual_amount: number | null;
  water_annual_amount: number | null;
  gas_annual_amount: number | null;
  general_services_amount: number | null;
  owner_nationality: string | null;

  raw_text_preview: string;
}

export interface EjarImportResult {
  filename: string;
  success: boolean;
  data?: EjarContractExtracted;
  error?: string;
}

export interface EjarImportResponse {
  total: number;
  successful: number;
  failed: number;
  results: EjarImportResult[];
}

/**
 * Upload EJAR contract PDFs to the backend for AI-powered data extraction.
 * Returns structured data for each file to be reviewed before saving to Supabase.
 */
export async function ejarImportExtract(
  files: File[],
  opts?: { strategy?: 'llm' | 'regex' },
): Promise<EjarImportResponse> {
  if (!API_URL) {
    // Mock response — only when no backend URL is configured (pure local dev)
    return {
      total: files.length,
      successful: files.length,
      failed: 0,
      results: files.map((f, i) => ({
        filename: f.name,
        success: true,
        data: {
          ejar_contract_number: `MOCK-${Date.now()}-${i}`,
          contract_type: 'residential',
          start_date: '2025-01-01',
          end_date: '2026-01-01',
          annual_rent: 24000,
          total_amount: 25200,
          vat_amount: 1200,
          payment_frequency: 'monthly',
          payment_schedule: [
            { due_date: '2025-01-01', amount: 2100, installment_number: 1 },
            { due_date: '2025-02-01', amount: 2100, installment_number: 2 },
          ],
          property_address: 'الرياض، حي النرجس',
          deed_number: '123456789',
          deed_issuer: null,
          deed_issue_date: null,
          deed_type: null,
          national_address: 'RIAD1234',
          property_type: 'عمارة',
          property_usage: 'سكني',
          property_units_count: 4,
          property_floors_count: 2,
          unit_number: '101',
          unit_type: 'شقة',
          floor_number: '1',
          area_sqm: 120,
          units: [{ unit_number: '101', unit_type: 'شقة', area_sqm: 120, floor_number: '1' }],
          tenant_name: 'محمد عبدالله',
          tenant_id_number: '1234567890',
          tenant_phone: '0501234567',
          tenant_type: 'individual',
          tenant_commercial_name: null,
          owner_name: 'عبدالرحمن البتيري',
          owner_id_number: '1234567890',
          tenant_nationality: null,
          tenant_id_type: null,
          tenant_email: null,
          organization_type: null,
          unified_number: null,
          cr_no: null,
          cr_date: null,
          brokerage_fee: null,
          security_deposit: null,
          number_of_payments: null,
          electricity_annual_amount: null,
          water_annual_amount: null,
          gas_annual_amount: null,
          general_services_amount: null,
          owner_nationality: null,
          raw_text_preview: 'نص العقد التجريبي...',
        },
      })),
    };
  }

  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const qs = opts?.strategy ? `?strategy=${opts.strategy}` : '';
  const response = await fetch(`${API_URL}/ejar-import/extract${qs}`, {
    method: 'POST',
    headers: {
      'X-API-Key': API_KEY,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await jsonOrEmpty(response);
    throw new Error((error as { message?: string }).message || 'فشل في استخراج بيانات العقود');
  }

  return safeJson(response);
}

// ============================================================================
// Hatif (Voxa) — frontend-facing /hatif/* endpoints on the NestJS gateway.
//
// Auth: X-API-Key header (same pattern as RER/REGA above). Backend forwards
// to Voxa via the HatifIntegrationModule, which owns the OAuth client
// credentials. The frontend never sees Voxa secrets directly.
// ============================================================================

// Accepts any plain object — typed interfaces (PagedQuery, HatifListConversationsQuery, …)
// satisfy `object` without an `as Record<string, unknown>` cast at every call site.
function hatifQueryString(params?: object): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    usp.append(key, String(value));
  }
  const q = usp.toString();
  return q ? `?${q}` : '';
}

// Typed error for /hatif/* failures so the Sentry guard chain can recognize
// upstream-dependency faults (424 config fault / 502 outage — the statuses the
// API's HatifApiError mapping produces) by status instead of message text.
// Matched by NAME in networkErrors.ts (isHatifUpstreamError) so that module
// stays import-free of this one.
export class HatifRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'HatifRequestError';
  }
}

// Every /hatif/* call is per-company: the required companyId is sent as the
// `X-Company-Id` header so the backend guard resolves that company's Hatif
// credentials + token. The user's Bearer JWT is required — the guard checks
// the caller is staff of that company (the API key alone ships in the bundle).
async function hatifFetch<T>(
  companyId: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  if (!API_URL) {
    throw new Error('API_URL is not configured — Hatif calls require a NestJS backend');
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new HatifRequestError('Not authenticated', 401);
  }
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      'X-API-Key': API_KEY,
      'X-Company-Id': companyId,
      ...(init.headers ?? {}),
    },
  });
  if (!response.ok) {
    const error = (await jsonOrEmpty(response)) as {
      message?: string;
      error?: string;
      code?: string;
    };
    throw new HatifRequestError(
      error.message || error.error || `Hatif request failed (${response.status})`,
      response.status,
      error.code,
    );
  }
  // 204 No Content (delete tag, etc.)
  if (response.status === 204) return undefined as T;
  return (await safeJson(response)) as T;
}

// --- Channels --------------------------------------------------------------

export function hatifGetChannels(
  companyId: string,
  query?: PagedQuery,
): Promise<PagedResult<ChannelItem>> {
  return hatifFetch<PagedResult<ChannelItem>>(
    companyId,
    `/hatif/channels${hatifQueryString(query)}`,
  );
}

// --- Conversations ---------------------------------------------------------

export function hatifListConversations(
  companyId: string,
  channelId: string,
  query?: HatifListConversationsQuery,
): Promise<PagedResult<HatifConversation>> {
  return hatifFetch<PagedResult<HatifConversation>>(
    companyId,
    `/hatif/channels/${channelId}/conversations${hatifQueryString(query)}`,
  );
}

export function hatifGetConversation(
  companyId: string,
  conversationId: string,
): Promise<HatifConversation> {
  return hatifFetch<HatifConversation>(
    companyId,
    `/hatif/conversations/${conversationId}`,
  );
}

export function hatifCreateConversation(
  companyId: string,
  body: HatifCreateConversationRequest,
): Promise<HatifCreateConversationResult> {
  return hatifFetch<HatifCreateConversationResult>(companyId, `/hatif/conversations`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function hatifGetConversationTimeline(
  companyId: string,
  conversationId: string,
  query?: SortedPagedQuery,
): Promise<PagedResult<HatifConversationTimelineEvent>> {
  return hatifFetch<PagedResult<HatifConversationTimelineEvent>>(
    companyId,
    `/hatif/conversations/${conversationId}/timeline${hatifQueryString(query)}`,
  );
}

export function hatifAssignConversation(
  companyId: string,
  conversationId: string,
  body: HatifAssignConversationRequest,
): Promise<unknown> {
  return hatifFetch<unknown>(
    companyId,
    `/hatif/conversations/${conversationId}/assign`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

// --- WhatsApp send ---------------------------------------------------------

export function hatifSendWhatsAppText(
  companyId: string,
  body: HatifSendWhatsAppTextRequest,
): Promise<HatifSendWhatsAppTextResult> {
  return hatifFetch<HatifSendWhatsAppTextResult>(
    companyId,
    `/hatif/whatsapp/send-text`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function hatifSendWhatsAppTemplate(
  companyId: string,
  body: HatifSendWhatsAppTemplateRequest,
): Promise<HatifSendWhatsAppTemplateResult> {
  return hatifFetch<HatifSendWhatsAppTemplateResult>(
    companyId,
    `/hatif/whatsapp/send-template`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

// Live, Meta-approved WhatsApp templates for the platform WABA — the real
// content + approval status + version (vs the seeded display copy, which
// drifts from Meta). Powers the admin "Notification templates" page.
export function hatifListWhatsAppTemplates(
  companyId: string,
): Promise<VoxaTemplate[]> {
  return hatifFetch<VoxaTemplate[]>(companyId, `/hatif/whatsapp/templates`);
}

// --- Tags ------------------------------------------------------------------

export function hatifListTags(
  companyId: string,
  query?: SortedPagedQuery,
): Promise<PagedResult<HatifTag>> {
  return hatifFetch<PagedResult<HatifTag>>(
    companyId,
    `/hatif/tags${hatifQueryString(query)}`,
  );
}

export function hatifCreateTag(
  companyId: string,
  body: HatifCreateTagRequest,
): Promise<HatifTag> {
  return hatifFetch<HatifTag>(companyId, `/hatif/tags`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function hatifUpdateTag(
  companyId: string,
  id: string,
  body: HatifUpdateTagRequest,
): Promise<HatifTag> {
  return hatifFetch<HatifTag>(companyId, `/hatif/tags/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function hatifDeleteTag(companyId: string, id: string): Promise<void> {
  return hatifFetch<void>(companyId, `/hatif/tags/${id}`, { method: 'DELETE' });
}

// --- Contact ---------------------------------------------------------------

export function hatifGetContactById(
  companyId: string,
  contactId: string,
): Promise<Contact> {
  return hatifFetch<Contact>(companyId, `/hatif/contacts/${contactId}`);
}

export function hatifSearchContacts(
  companyId: string,
  body: HatifSearchContactsRequest,
  query?: PagedQuery,
): Promise<PagedResult<Contact>> {
  return hatifFetch<PagedResult<Contact>>(
    companyId,
    `/hatif/contacts/search${hatifQueryString(query)}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function hatifSetContactPropertyValue(
  companyId: string,
  contactId: string,
  propertyId: string,
  body: HatifSetContactPropertyValueRequest,
): Promise<Contact> {
  return hatifFetch<Contact>(
    companyId,
    `/hatif/contacts/${contactId}/properties/${propertyId}`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
    },
  );
}

export function hatifDeleteContactPropertyValue(
  companyId: string,
  contactId: string,
  propertyId: string,
): Promise<Contact> {
  return hatifFetch<Contact>(
    companyId,
    `/hatif/contacts/${contactId}/properties/${propertyId}`,
    { method: 'DELETE' },
  );
}

// ============================================================================
// Notifications dispatcher — frontend-facing entry point on the NestJS gateway.
//
// Used by the invite popup, the invoice "Send" button, and any future manual
// notification flows. Routes to /notifications/dispatch on Nest, which
// applies the super-admin channel gate, the user-pref / channels-override,
// and the contact-info gate before fanning out to SMS/Email/WhatsApp/In-app.
// ============================================================================

export async function dispatchNotification(
  request: NotificationDispatchRequest,
): Promise<NotificationDispatchResponse> {
  if (!API_URL) {
    throw new Error(
      'API_URL is not configured — notification dispatch requires the NestJS backend',
    );
  }
  const response = await fetch(`${API_URL}/notifications/dispatch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const error = (await jsonOrEmpty(response)) as {
      message?: string;
      error?: string;
    };
    throw new Error(
      error.message ||
        error.error ||
        `Notification dispatch failed (${response.status})`,
    );
  }
  return (await safeJson(response)) as NotificationDispatchResponse;
}

// Fetch the VAPID public key from Nest GET /notifications/vapid-public-key (public endpoint).
// Returns null when no backend is configured so callers degrade gracefully.
export async function getVapidPublicKey(): Promise<string | null> {
  if (!API_URL) {
    // Local dev without a backend — push subscriptions won't work, but the
    // app should still load without throwing.
    return null;
  }
  const response = await fetch(`${API_URL}/notifications/vapid-public-key`, {
    method: 'GET',
    headers: {
      'X-API-Key': API_KEY,
    },
  });
  if (!response.ok) {
    return null;
  }
  const body = (await safeJson(response)) as { publicKey?: string; error?: string };
  return body.publicKey ?? null;
}

export interface SetCompanyHatifCredentialsRequest {
  companyId: string;
  clientId?: string;
  // Omit/empty to keep the existing encrypted secret.
  clientSecret?: string;
  whatsappChannelId?: string;
  /** Voxa workspace UUID — visible in Hatif's "API connect" settings
   * page next to the channel id. Nest stores it on the credentials row
   * and reads it back when an inbound webhook arrives so the audit row
   * gets the right `company_id`. */
  workspaceId?: string;
  /** WhatsApp Business Account id that holds this company's templates — powers
   * the live templates view. Found in Hatif's "API connect" settings.
   * REQUIRED: every company must have a WABA configured. */
  wabaId: string;
  scope?: string;
  baseUrl?: string;
  enabled: boolean;
  updatedBy?: string;
}

// Super-admin: write a company's Hatif (WhatsApp) credentials. The secret is
// encrypted server-side; this never reads secrets back.
export async function setCompanyHatifCredentials(
  request: SetCompanyHatifCredentialsRequest,
): Promise<{ ok: true }> {
  if (!API_URL) {
    throw new Error(
      'API_URL is not configured — saving Hatif credentials requires the NestJS backend',
    );
  }
  const response = await fetch(
    `${API_URL}/notifications/admin/company-hatif-credentials`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify(request),
    },
  );
  if (!response.ok) {
    const error = (await jsonOrEmpty(response)) as {
      message?: string;
      error?: string;
    };
    throw new Error(
      error.message ||
        error.error ||
        `Saving Hatif credentials failed (${response.status})`,
    );
  }
  return (await safeJson(response)) as { ok: true };
}

// ============================================================================
// Identity + nest-side account-lifecycle endpoints (folded in from
// origin/staging during the merge — these landed independently of the Hatif
// + dispatcher work above).
// ============================================================================

export async function nestUpdateEmail(email: string): Promise<{ success: boolean }> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${requireApiUrl()}/auth/update-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      'X-API-Key': API_KEY,
    },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const body = (await jsonOrEmpty(response)) as { message?: string; code?: string };
    throw new Error(body.message || 'Failed to update email');
  }
  return safeJson(response);
}

export async function nestResendEmail(): Promise<{ success: boolean }> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${requireApiUrl()}/auth/resend-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      'X-API-Key': API_KEY,
    },
  });
  if (!response.ok) {
    const body = (await jsonOrEmpty(response)) as { message?: string; code?: string };
    throw new Error(body.message || 'Failed to resend confirmation');
  }
  return safeJson(response);
}

export async function nestSignup(
  phone: string,
  code: string,
  opts?: { email?: string; password?: string; metadata?: Record<string, unknown> },
): Promise<NestSessionTokens> {
  const response = await fetch(`${requireApiUrl()}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
    body: JSON.stringify({ phone, code, ...opts }),
  });
  if (!response.ok) {
    throw new Error(await nestErrorMessage(response, 'Signup failed'));
  }
  return safeJson(response);
}

export async function identityReserveUser(payload: {
  phone: string;
  national_id?: string;
  full_name?: string;
  // Park-at-invite: the inviter's company + the invited role. When supplied,
  // reserveUser grants the user_roles row up front so the invitee can log in
  // immediately (no "account not set up" / /no-access).
  company_id?: string;
  role?: string;
  custom_role?: string;
}): Promise<{ user_id: string }> {
  if (!API_URL) {
    throw new Error(
      'VITE_API_URL is not configured — the identity API requires a backend URL for reserve-user.',
    );
  }
  // Forward the caller's session JWT so the backend can authorize a role-park
  // against the target company (#2215). Without it, reserve-user can only create
  // a bare shell — it will 403 any company_id/role park. All park callers (the
  // invite flow) are authenticated admins, so a session is present here.
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const response = await fetch(`${identityApiUrl()}/reserve-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errBody = (await jsonOrEmpty(response)) as { message?: string };
    throw new Error(errBody.message ?? 'Reserve user failed');
  }
  return safeJson(response);
}

// ============================================================================
// Finance — HyperPay payment endpoints (b1 repoint: edge fns → Nest).
//
// All calls go to POST /finance/hyperpay/* on the NestJS gateway.
// When API_URL is not configured (local dev without a backend), the functions
// return structured mocks so local UI development doesn't silently break — per
// runbook §3.2 (previously-retired edge-fn fallback callers must not be
// abandoned when the Edge Runtime shuts down).
//
// Auth:
//   - hyperpay/checkout + hyperpay/status → SupabaseAuthGuard (needs session)
//   - hyperpay/invoice-checkout + hyperpay/invoice-status → public (invoice_id is the secret)
//   - hyperpay/master-checkout + hyperpay/master-status → public
//   - hyperpay/submit-proof → public
// ============================================================================

type HyperPayBrand = 'MADA' | 'VISA' | 'APPLEPAY' | 'STC_PAY';

export interface HyperPayCheckoutResult {
  checkoutId: string;
  brands: string;
  hyperpayBase: string;
}

export interface HyperPayStatusResult {
  success: boolean;
  pending: boolean;
  status: string;
  result_code: string;
  result_description: string;
  payment_brand: string | null;
}

export interface InvoiceSummaryResult {
  id: string;
  invoice_number: string | null;
  amount: number;
  balance: number;
  status: string;
  due_date: string;
  description: string | null;
  tenant_name: string;
  unit_number: string | null;
  property_name: string | null;
  receipt: Record<string, unknown> | null;
}

export interface InvoiceCheckoutResult extends HyperPayCheckoutResult {
  invoice: InvoiceSummaryResult;
}

export interface MasterSummaryResult {
  id: string;
  master_invoice_number: string | null;
  total_amount: number;
  status: string;
  notes: string | null;
  client_name: string;
  items: Array<{
    id: string;
    invoice_number: string | null;
    total_amount: number;
    balance: number;
    status: string;
    due_date: string;
  }>;
}

export interface MasterCheckoutResult extends HyperPayCheckoutResult {
  master: MasterSummaryResult;
}

export interface SubmitProofResult {
  ok: boolean;
  scheduled_payment_id: string | null;
  proof_path: string;
}

// ── Typed finance API error ───────────────────────────────────────────────────

/**
 * Thrown by financePost() when Nest returns a non-2xx response.
 *
 * When Nest sends a coded error (e.g. fin-003 / fin-006 — invoice not payable)
 * the `code` and `statusReason` fields are populated so callers can route the
 * user to the correct UI state without parsing the human-readable message.
 *
 * NOT_PAYABLE reasons:
 *   "paid" / "overpaid" → invoice already settled — show the paid step
 *   "cancelled"         → invoice was cancelled  — show the cancelled step
 *   "zero_balance"      → balance is zero (treated as paid) — show the paid step
 *   any other invoice status ("issued" / "partial" / "overdue" — masters only,
 *   #3477) → still owed but not payable online — show a not-payable message,
 *   never the cancelled step, and never capture to Sentry.
 */
export class FinanceApiError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly statusReason?: 'paid' | 'overpaid' | 'cancelled' | 'zero_balance' | 'issued' | 'partial' | 'overdue' | string,
  ) {
    super(message);
    this.name = 'FinanceApiError';
  }
}

// ── Helper: POST to a /finance/hyperpay/* endpoint ───────────────────────────

async function financePost<T>(
  path: string,
  body: Record<string, unknown>,
  accessToken?: string | null,
): Promise<T> {
  if (!API_URL) {
    throw new Error(
      'VITE_API_URL is not configured — HyperPay payment calls require a NestJS backend URL.',
    );
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  const response = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await jsonOrEmpty(response) as Record<string, unknown>;
    const message = (err['message'] as string | undefined) || `Finance API error (${response.status})`;
    const code = err['code'] as string | undefined;
    // fin-003 / fin-006 — invoice (or master invoice) is not payable.
    // Nest embeds the status_reason as a suffix in the message string; we
    // re-surface it as a typed field so the caller can route without parsing.
    if (code === 'fin-003' || code === 'fin-006') {
      // Nest sends a structured `statusReason` field (#3477). The message-suffix
      // regex stays as a fallback for a not-yet-redeployed API; it enumerates
      // every invoice_status member plus zero_balance — including `due`, which
      // the API never emits today — so no reason is silently dropped if the
      // gate's emit set ever widens. Case-insensitive — tolerant of any
      // capitalisation from the server.
      const structured = typeof err['statusReason'] === 'string' ? err['statusReason'] : undefined;
      const reasonMatch = message.match(/:\s*(issued|due|overdue|partial|paid|overpaid|cancelled|zero_balance)\s*$/i);
      const statusReason = structured ?? (reasonMatch ? reasonMatch[1].toLowerCase() : undefined);
      throw new FinanceApiError(message, code, statusReason);
    }
    throw new FinanceApiError(message, code);
  }
  return (await safeJson(response)) as T;
}

// ── 1. Subscription checkout (POST /finance/hyperpay/checkout) ────────────────

export async function financeHyperPayCheckout(params: {
  amount: number;
  company_id: string;
  payment_brand: HyperPayBrand;
  billing_cycle: 'monthly' | 'annual';
  email?: string;
  unit_count?: number;
  user_count?: number;
  customer_given_name?: string;
  customer_surname?: string;
  customer_mobile?: string;
  auth_user_id?: string;
  billing_street?: string;
  billing_city?: string;
  billing_state?: string;
  billing_country?: string;
  billing_postcode?: string;
}): Promise<HyperPayCheckoutResult> {
  if (!API_URL) {
    // Local-dev mock — lets the subscribe UI render without a backend.
    return {
      checkoutId: `mock-checkout-${Date.now()}`,
      brands: params.payment_brand,
      hyperpayBase: 'https://test.oppwa.com',
    };
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return financePost<HyperPayCheckoutResult>(
    '/finance/hyperpay/checkout',
    params as Record<string, unknown>,
    session?.access_token,
  );
}

// ── Master Card action checkout (POST /finance/hyperpay/action-checkout) — Epic G ──
// One-time pay for a maintenance/cleaning action. Guarded (owner session). In dev
// (HyperPay unconfigured) the backend mock-settles and returns { settled: true }.
export async function financeActionCheckout(params: {
  action_id: string;
  payment_brand?: HyperPayBrand;
}): Promise<{
  mock?: boolean;
  settled?: boolean;
  amount: number;
  checkout_id?: string;
  checkoutId?: string;
  widgetBaseUrl?: string;
  brands?: string;
}> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return financePost(
    '/finance/hyperpay/action-checkout',
    params as Record<string, unknown>,
    session?.access_token,
  );
}

// ── 2. Subscription status (POST /finance/hyperpay/status) ───────────────────

export async function financeHyperPayStatus(params: {
  checkout_id: string;
  company_id: string;
  resource_path?: string | null;
}): Promise<HyperPayStatusResult> {
  if (!API_URL) {
    return { success: false, pending: true, status: 'pending', result_code: '', result_description: '', payment_brand: null };
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const body: Record<string, unknown> = {
    checkout_id: params.checkout_id,
    company_id: params.company_id,
  };
  if (params.resource_path) body['resource_path'] = params.resource_path;
  return financePost<HyperPayStatusResult>(
    '/finance/hyperpay/status',
    body,
    session?.access_token,
  );
}

// ── 3. Invoice checkout (POST /finance/hyperpay/invoice-checkout) — public ───

export async function financeInvoiceCheckout(params: {
  invoice_id: string;
  payment_brand?: HyperPayBrand;
  summary_only?: boolean;
  customer_given_name?: string;
  customer_surname?: string;
  customer_email?: string;
}): Promise<{ invoice: InvoiceSummaryResult } | InvoiceCheckoutResult> {
  if (!API_URL) {
    const mockInvoice: InvoiceSummaryResult = {
      id: params.invoice_id,
      invoice_number: 'MOCK-INV-001',
      amount: 1000,
      balance: 1000,
      status: 'due',
      due_date: new Date().toISOString().split('T')[0],
      description: 'Mock invoice',
      tenant_name: 'Test Tenant',
      unit_number: '101',
      property_name: 'Test Property',
      receipt: null,
    };
    if (params.summary_only) return { invoice: mockInvoice };
    return {
      checkoutId: `mock-checkout-${Date.now()}`,
      brands: params.payment_brand || 'MADA',
      hyperpayBase: 'https://test.oppwa.com',
      invoice: mockInvoice,
    };
  }
  return financePost<{ invoice: InvoiceSummaryResult } | InvoiceCheckoutResult>(
    '/finance/hyperpay/invoice-checkout',
    params as Record<string, unknown>,
  );
}

// ── 4. Invoice status (POST /finance/hyperpay/invoice-status) — public ───────

export async function financeInvoiceStatus(params: {
  checkout_id: string;
  invoice_id: string;
  resource_path?: string | null;
}): Promise<HyperPayStatusResult & { gateway_response?: unknown }> {
  if (!API_URL) {
    return { success: false, pending: true, status: 'pending', result_code: '', result_description: '', payment_brand: null };
  }
  const body: Record<string, unknown> = {
    checkout_id: params.checkout_id,
    invoice_id: params.invoice_id,
  };
  if (params.resource_path) body['resource_path'] = params.resource_path;
  return financePost<HyperPayStatusResult & { gateway_response?: unknown }>(
    '/finance/hyperpay/invoice-status',
    body,
  );
}

// ── 5. Master checkout (POST /finance/hyperpay/master-checkout) — public ─────

export async function financeMasterCheckout(params: {
  master_invoice_id: string;
  payment_brand?: HyperPayBrand;
  summary_only?: boolean;
  customer_given_name?: string;
  customer_surname?: string;
  customer_email?: string;
}): Promise<{ master: MasterSummaryResult } | MasterCheckoutResult> {
  if (!API_URL) {
    const mockMaster: MasterSummaryResult = {
      id: params.master_invoice_id,
      master_invoice_number: 'MOCK-MINV-001',
      total_amount: 2000,
      status: 'due',
      notes: null,
      client_name: 'Test Client',
      items: [],
    };
    if (params.summary_only) return { master: mockMaster };
    return {
      checkoutId: `mock-checkout-${Date.now()}`,
      brands: params.payment_brand || 'MADA',
      hyperpayBase: 'https://test.oppwa.com',
      master: mockMaster,
    };
  }
  return financePost<{ master: MasterSummaryResult } | MasterCheckoutResult>(
    '/finance/hyperpay/master-checkout',
    params as Record<string, unknown>,
  );
}

// ── 6. Master status (POST /finance/hyperpay/master-status) — public ─────────

export async function financeMasterStatus(params: {
  checkout_id: string;
  master_invoice_id: string;
  resource_path?: string | null;
}): Promise<HyperPayStatusResult & { receipts_created?: number; already_processed?: boolean; gateway_response?: unknown }> {
  if (!API_URL) {
    return { success: false, pending: true, status: 'pending', result_code: '', result_description: '', payment_brand: null };
  }
  const body: Record<string, unknown> = {
    checkout_id: params.checkout_id,
    master_invoice_id: params.master_invoice_id,
  };
  if (params.resource_path) body['resource_path'] = params.resource_path;
  return financePost<HyperPayStatusResult & { receipts_created?: number; already_processed?: boolean; gateway_response?: unknown }>(
    '/finance/hyperpay/master-status',
    body,
  );
}

// ── 7. Submit payment proof (POST /finance/hyperpay/submit-proof) — public ───

export async function financeSubmitPaymentProof(params: {
  invoice_id: string;
  file_base64: string;
  file_name: string;
  content_type: string;
  reference_number?: string | null;
}): Promise<SubmitProofResult> {
  if (!API_URL) {
    return { ok: true, scheduled_payment_id: null, proof_path: 'mock/path' };
  }
  return financePost<SubmitProofResult>(
    '/finance/hyperpay/submit-proof',
    params as Record<string, unknown>,
  );
}

// ============================================================================
// Property Media — S3-backed image uploads (PR #2863)
//
// SCOPE: ONLY property/unit photo uploads. Deed, contract PDF, ID document,
// and all other regulated-document uploads are NEVER routed here.
//
// Auth: SupabaseAuthGuard — Supabase JWT as Bearer token (same pattern as
// nestUpdatePassword, financeHyperPayCheckout, etc.).
// ============================================================================

// Wire contract for POST /property-media/images lives in @dallali/shared
// (single FE<->BE source of truth); imported above for local use and
// re-exported here so existing `@/services/api` imports keep resolving.
export type { PropertyMediaEntityType, UploadPropertyImageResponse };

/**
 * Upload a single property/unit image to S3 via the NestJS property-media endpoint.
 *
 * Auth: Supabase JWT Bearer token — call supabase.auth.getSession() to get it,
 * exactly as other authenticated NestJS calls (nestUpdatePassword, etc.).
 *
 * @param file       - The image file (JPEG/PNG/WebP/GIF, max 10 MB)
 * @param entityType - "property" or "unit"
 * @param entityId   - Optional UUID of the property/unit (omit for pre-create uploads)
 * @returns { url, key } — url is a plain public CDN URL safe for <img src>
 * @throws Error with code 'pmedia-005' message when S3 creds are not provisioned (503)
 */
export async function uploadPropertyImageViaApi(
  file: File,
  entityType: PropertyMediaEntityType,
  entityId?: string,
): Promise<UploadPropertyImageResponse> {
  if (!API_URL) {
    // Local dev without a NestJS backend — return a mock CDN URL so the
    // image-upload UI still works in mock mode. The blob URL is ephemeral
    // (it dies on page reload), so if the S3 flag is actually ON this is a
    // misconfiguration that would persist dead URLs to the DB — warn loudly.
    if (import.meta.env.VITE_USE_S3_PROPERTY_IMAGES === 'true') {
      console.warn(
        '[property-media] VITE_USE_S3_PROPERTY_IMAGES=true but VITE_API_URL is unset — ' +
          'falling back to an ephemeral blob URL (local dev only); uploads will NOT persist.',
      );
    }
    return {
      url: URL.createObjectURL(file),
      key: `mock/${entityType}/${Date.now()}_${file.name}`,
    };
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  formData.append('image', file);
  formData.append('entity_type', entityType);
  if (entityId) {
    formData.append('entity_id', entityId);
  }

  const response = await fetch(`${API_URL}/property-media/images`, {
    method: 'POST',
    headers: {
      // No Content-Type header — fetch sets multipart/form-data boundary automatically
      Authorization: `Bearer ${session.access_token}`,
      'X-API-Key': API_KEY,
    },
    body: formData,
  });

  if (!response.ok) {
    const body = (await jsonOrEmpty(response)) as {
      message?: string;
      code?: string;
    };
    throw new Error(
      body.message ||
        `Property media upload failed (${response.status})`,
    );
  }

  return (await safeJson(response)) as UploadPropertyImageResponse;
}

/**
 * Delete multiple property/unit media files by their CDN URLs via the NestJS
 * DELETE /property-media/images endpoint. Sends a JSON body `{ urls: string[] }`.
 *
 * Used only when USE_S3_PROPERTY_IMAGES is enabled and the URLs are S3 CDN URLs
 * (not Supabase Storage). Supabase-hosted media is deleted via the Supabase client.
 *
 * @param urls - Array of S3 CDN URLs to delete (max 100 per call, enforced server-side)
 * @throws Error with a descriptive message on non-204 response
 */
export async function deletePropertyMediaViaApi(urls: string[]): Promise<void> {
  if (!API_URL || urls.length === 0) return;

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const response = await fetch(`${API_URL}/property-media/images`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      'X-API-Key': API_KEY,
    },
    body: JSON.stringify({ urls }),
  });

  if (!response.ok && response.status !== 204) {
    const body = (await jsonOrEmpty(response)) as { message?: string };
    throw new Error(body.message || `Property media delete failed (${response.status})`);
  }
}

/**
 * Delete a contract document from the `media` bucket via the guarded NestJS
 * `DELETE /documents/contract-documents` endpoint.
 *
 * C1 phase 1 (#3035): the `media` bucket's storage.objects RLS no longer lets
 * an authenticated client `.remove()` an arbitrary object directly — DELETE
 * is service_role-only now. This is the replacement path for
 * ContractDocumentsTab's explicit document-delete button and its
 * Ejar-candidate cancel cleanup. The server re-verifies the contract belongs
 * to the caller's company before deleting (company_id is never taken from
 * the client).
 *
 * @param path - The `media` bucket-relative storage path, e.g.
 *   `contracts/{contractId}/{filename}`.
 * @throws Error when the backend is not configured, or on a non-2xx response.
 */
export async function deleteContractDocumentViaApi(path: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const response = await fetch(`${requireApiUrl()}/documents/contract-documents`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      'X-API-Key': API_KEY,
    },
    body: JSON.stringify({ path }),
  });

  if (!response.ok) {
    throw new Error(
      await nestErrorMessage(response, `Contract document delete failed (${response.status})`),
    );
  }
}
